import React from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ProfilePicture from './ProfilePicture';
import { usePermissions } from '../../features/auth/hooks/usePermissions';

/**
 * StaffMember component for displaying a staff member with their profile picture and name
 * This component can be used anywhere staff members need to be displayed consistently
 * Clicking on it navigates to the staff member's details page
 *
 * @param {Object} props
 * @param {Object|null} props.staffMember - Staff member object with name, profile_picture_file_id, and staff_member_id
 * @param {string} props.staffMember.name - Name of the staff member
 * @param {string} props.staffMember.staff_member_id - ID of the staff member
 * @param {string|null} props.staffMember.profile_picture_file_id - Profile picture file ID
 * @param {number} props.avatarSize - Size of the avatar in pixels (default: 40)
 * @param {string} props.imageSize - Size parameter for the image URL (default: 64x64)
 * @param {number} props.gap - Gap between avatar and name (default: 2)
 * @param {string} props.nameVariant - Typography variant for the name (default: 'body1')
 * @param {Object} props.nameSx - Additional sx props for the name Typography
 * @param {Object} props.sx - Additional sx props for the container Box
 * @param {string} props.bgcolor - Background color for the avatar (default: 'primary.main')
 * @param {boolean} props.clickable - Whether to enable navigation on click (default: true)
 * @returns {JSX.Element}
 */
export default function StaffMember({
  staffMember,
  avatarSize = 40,
  imageSize = '64x64',
  gap = 2,
  nameVariant = 'body1',
  nameSx = {},
  sx = {},
  bgcolor = 'primary.main',
  clickable: clickableProp = true,
  ...otherProps
}) {
  const navigate = useNavigate();
  const { hasScope } = usePermissions();
  // The details page needs staff:read; without it the name is just a name
  const clickable = clickableProp && hasScope('staff:read');

  // Handle null/undefined staff member
  if (!staffMember) {
    return null;
  }

  const handleClick = () => {
    if (clickable && staffMember.staff_member_id) {
      navigate(`/staff/${staffMember.staff_member_id}`);
    }
  };

  const containerSx = {
    display: 'flex',
    alignItems: 'center',
    gap,
    ...(clickable ? {
      cursor: 'pointer',
      '&:hover': {
        opacity: 0.8,
      },
    } : {}),
    ...sx,
  };

  return (
    <Box sx={containerSx} onClick={handleClick} {...otherProps}>
      <ProfilePicture
        profilePictureFileId={staffMember.profile_picture_file_id}
        name={staffMember.name}
        size={avatarSize}
        imageSize={imageSize}
        bgcolor={bgcolor}
        color={staffMember?.color}
      />
      <Typography
        variant={nameVariant}
        sx={{
          fontWeight: 500,
          ...nameSx,
        }}
      >
        {staffMember.name}
      </Typography>
    </Box>
  );
}
